/**
 * CRM API 客户端 — 从 @crm/shared 重导出
 *
 * 本文件是前端应用与共享 API 层之间的桥接模块。所有 API 调用逻辑已迁移到
 * packages/shared/src/api/ 目录，此文件仅负责：
 *
 * 1. 配置共享 API 层的全局回调（错误提示 toast、认证过期重定向）
 * 2. 将共享 API 层的所有导出原样重导出，供前端各组件统一从 './api.js' 引入
 *
 * 设计决策：
 * - 前端组件统一 import { xxxApi, get, post, ... } from './api.js'，
 *   不直接从 @crm/shared 导入，确保配置在此处集中完成
 * - 认证过期时重定向到 /crm/login，与路由系统的路径约定一致
 * - 错误消息通过 toast 组件统一展示，区分 error / warning / info 三种级别
 *
 * 关联模块：
 * - packages/shared/src/api/client.js — 核心 HTTP 客户端和 token 管理
 * - packages/shared/src/api/*.js — 各业务域 API 命名空间
 * - ./components/toast.js — 全局 Toast 通知组件
 */

import { toast } from './components/toast.js';
import { configureApi } from '@crm/shared/api/client.js';

/**
 * 配置共享 API 层的错误处理和认证过期回调
 *
 * - onError: API 请求失败时的全局错误提示，根据 kind 选择不同的 toast 级别
 * - onAuthExpired: 当 JWT token 过期或无效时，重定向到登录页
 */
configureApi({
  onError: (message, kind) => {
    if (kind === 'warning') toast.warning(message);
    else if (kind === 'info') toast.info(message);
    else toast.error(message);
  },
  onAuthExpired: () => {
    // token 过期或无效 → 跳转登录页重新认证
    window.location.href = '/crm/login';
  },
});

// ==================== 重导出 client 工具 ====================
// 以下为 HTTP 客户端核心工具函数和常量，供各组件直接调用
export {
  API_BASE_URL,           // API 基础路径（默认 /api，由 Vite 代理到 /api/v1）
  setTokens,              // 将服务端令牌保存在当前页面内存中
  clearTokens,            // 清除 token（登出时调用）
  isLoggedIn,             // 检查当前是否已登录
  saveUser,               // 保存用户信息到 localStorage
  loadUser,               // 从 localStorage 读取用户信息
  get,                    // HTTP GET 请求封装
  post,                   // HTTP POST 请求封装
  put,                    // HTTP PUT 请求封装
  patch,                  // HTTP PATCH 请求封装
  del,                    // HTTP DELETE 请求封装
  downloadBlob,           // 下载二进制文件（Blob）
  uploadFile,             // 上传文件（FormData）
} from '@crm/shared/api/client.js';

// ==================== 重导出 API 命名空间 ====================
// 以下为各业务域的 API 对象，每个对象封装了对应模块的 CRUD 和业务操作
export { authApi, userApi, apiTokenApi } from '@crm/shared/api/auth.js';          // 认证与用户管理 API
export { customerApi, categoryApi } from '@crm/shared/api/customer.js';       // 客户与客户分类 API
export {
  emailApi,                    // 邮件收发与管理
  emailTagApi,                 // 邮件标签管理
  emailBlacklistApi,           // 邮件黑名单管理
  emailAccountApi,             // 邮箱账户管理（IMAP/SMTP）
  signatureApi,                // 邮件签名管理
  emailSendApi,                // 邮件发送操作
  emailThreadApi,              // 邮件对话线程管理
  taskApi,                     // 邮件任务（批量发送等异步任务）
  permissionApi,               // 权限与角色管理
  emailTemplateApi,            // 邮件模板管理
  emailTemplateCategoryApi,    // 邮件模板分类管理
  emailTaskApi,                // 邮件任务管理
  uploadTemplateAttachment,    // 上传模板附件
  uploadTemplateInlineImage,   // 上传模板内嵌图片
  uploadAttachment,            // 上传邮件附件
  uploadInlineImage,           // 上传邮件内嵌图片
} from '@crm/shared/api/email.js';
export {
  productApi,                 // 产品管理 API
  uploadProductImage,          // 上传产品图片
  uploadProductDocument,       // 上传产品文档
} from '@crm/shared/api/product.js';
export { dashboardApi } from '@crm/shared/api/dashboard.js';                  // 数据概览 API
export { newsApi, uploadNewsImage } from '@crm/shared/api/news.js';           // 新闻管理与图片上传
export { resourceApi, resourceCategoryApi } from '@crm/shared/api/resource.js'; // 资源库与分类管理
export { contactApi, followUpApi } from '@crm/shared/api/contact.js';         // 联系管理与客户跟进提醒
export { websiteApi, uploadAboutImage } from '@crm/shared/api/website.js';    // 官网数据 API
